'use client';

/**
 * PricingBenchmarkLadderCard — catalogue-wide pricing-benchmark ladder.
 *
 * Full-bleed canvas view, no selection required (mode11's 'Benchmarks'
 * explorer row mounts it via sel:benchmarks). Pure renderer over the
 * BFF's already-grouped, already-sorted, already-thresholded response
 * (catalog_pricing_benchmarks) — this component does NO grouping, NO
 * sorting, and NO N-gate/variant-flagging logic of its own. All of that
 * is business logic and lives server-side (klyde_intelligence.services.
 * pricing_benchmark.list_pricing_benchmarks_grouped), per BFF
 * discipline: React paints what's shipped.
 *
 * display_class per row:
 *   'full'         — three dots (budget/mid/premium) + a ceiling tick
 *   'thin'         — a single median dot, muted
 *   'insufficient' — no dot, just N — collapsed into one trailing line
 *                    per category rather than one blank row each (was
 *                    75% of the chart before this pass)
 * The bare-category fallback row (product_type=null) renders hatched
 * and always trails, even below the collapsed-insufficient line.
 *
 * Log scale is computed PER CATEGORY GROUP, not globally — a shared
 * global scale wastes most of a group's width on outliers from other
 * categories (cushion $59 / sofa $11.1k stretch every other group's
 * rows into a narrow middle band).
 *
 * @see klyde_edge/ui_config/composition/cards/search.py — content_key="pricing_benchmark_ladder"
 * @see klyde_edge/ui_config/handlers/catalog.py — catalog_pricing_benchmarks
 * @see klyde_edge/klyde_intelligence/services/pricing_benchmark.py — list_pricing_benchmarks_grouped
 *
 * @layer presentation
 * @arch_component_type card
 * @arch_domain canvas
 */

import { useMemo, useState } from 'react';
import { ChevronRight, Loader2, LineChart, Table2, XCircle } from 'lucide-react';
import { useStrategyData } from '@/lib/api/hooks';
import { useCapabilityStore } from '@/lib/stores/capability-store';
import { useCardIdentity } from '@/lib/design/card-chrome';
import { useCardExport } from '@/lib/design/card-export';
import { text, surfaceBase } from '@/lib/design/tokens';
import { cn } from '@/lib/utils/cn';
import { usePricingBenchmarkSelectionStore } from '@/lib/stores/pricing-benchmark-store';
import { useGraphSelectionStore } from '@/lib/stores/graph-selection-store';
import { api } from '@/lib/api/client';

interface PriceNeighbourProduct {
  semantic_id: string;
  name: string | null;
  sku: string | null;
  list_price: number | null;
  image_url: string | null;
}
interface PriceNeighbourhoodResponse {
  products: PriceNeighbourProduct[];
  percentile: number | null;
  n: number;
  below: number;
}
interface PricePick {
  category: string;
  productType: string | null;
  price: number;
  n: number;
  displayClass: 'full' | 'thin' | 'insufficient';
  budgetMedian: number | null;
  midMedian: number | null;
  premiumMedian: number | null;
}

const _TIER_N_GATE = 20; // mirrors pricing_benchmark.py's _MIN_N_FOR_TIERS

/**
 * Say the verdict, not the computation — the classification layer
 * already did the interpretation (budget/mid/premium tiers); this
 * turns that back into a sentence instead of handing the buyer a raw
 * percentile to re-interpret themselves. "Anchor" and "percentile"
 * never reach the UI. Confidence is stated plainly when the sample is
 * thin, the same honesty the chart's own grey dots already carry —
 * never a clean verdict built on a handful of data points.
 */
function describeBenchmarkPosition(
  pick: PricePick, live: { percentile: number | null; n: number; below: number },
): { headline: string; subtitle: string | null } {
  const label = pick.productType ?? pick.category;
  const thin = live.n < _TIER_N_GATE;
  const confidenceNote = thin ? ` (only ${live.n} priced examples)` : '';

  let verdict: string;
  if (pick.displayClass === 'full' && pick.budgetMedian != null && pick.premiumMedian != null && pick.midMedian != null) {
    if (pick.price <= pick.budgetMedian) {
      verdict = `Budget price for a ${label}`;
    } else if (pick.price >= pick.premiumMedian) {
      verdict = `Above typical for a ${label} — premium range`;
    } else if (pick.price > pick.midMedian) {
      verdict = `About typical for a ${label} — a bit above the middle`;
    } else if (pick.price < pick.midMedian) {
      verdict = `About typical for a ${label} — a bit below the middle`;
    } else {
      verdict = `Typical price for a ${label}`;
    }
  } else if (live.percentile != null) {
    // Thin/insufficient row — no persisted tier medians to compare
    // against, so fall back to where this price sits in the live
    // catalogue distribution, worded the same way, honesty attached.
    if (live.percentile < 33) verdict = `Below typical for a ${label}`;
    else if (live.percentile > 67) verdict = `Above typical for a ${label}`;
    else verdict = `About typical for a ${label}`;
  } else {
    verdict = `Price for a ${label}`;
  }

  const subtitle = live.percentile != null
    ? `${live.below} of ${live.n} similarly priced products cost less${confidenceNote}.`
    : confidenceNote ? `Only ${live.n} priced examples.` : null;

  return { headline: `${verdict}.`, subtitle };
}

interface BenchmarkRow {
  product_type: string | null;
  is_bare_category_fallback: boolean;
  display_class: 'full' | 'thin' | 'insufficient';
  n: number;
  grain: string;
  low: number | null;
  high: number | null;
  median: number | null;
  budget_median: number | null;
  mid_median: number | null;
  premium_median: number | null;
  ceiling: number | null;
  flagged_variant_of: string | null;
}
interface CategoryGroup {
  category: string;
  total_n: number;
  flagged_count: number;
  rows: BenchmarkRow[];
}
interface CatalogPricingBenchmarksResponse {
  currency: string;
  price_basis: string;
  as_of: string | null;
  sort: 'median' | 'spread';
  total_flagged: number;
  category_groups: CategoryGroup[];
}

function fmtPrice(v: number | null): string {
  if (v == null) return '—';
  return v >= 1000 ? `$${(v / 1000).toFixed(1)}k` : `$${Math.round(v)}`;
}

function scaleFor(rows: BenchmarkRow[]): [number, number] {
  const values = rows
    .flatMap((r) => [r.budget_median, r.mid_median, r.premium_median, r.ceiling, r.median])
    .filter((v): v is number => v != null && v > 0);
  if (values.length === 0) return [50, 10000];
  return [Math.min(...values) * 0.9, Math.max(...values) * 1.1];
}

/** Decade gridlines ($10/$100/$1k/...) within [min, max] — the actual
 *  readable axis this chart was missing (flagged three times: no ticks,
 *  no gridlines, no price readable off it). Log-scale decades are the
 *  natural gridline unit here, same as any log-price chart. */
function decadeTicks(min: number, max: number): number[] {
  const startExp = Math.floor(Math.log10(min));
  const endExp = Math.ceil(Math.log10(max));
  const ticks: number[] = [];
  for (let e = startExp; e <= endExp; e++) {
    const v = 10 ** e;
    if (v >= min && v <= max) ticks.push(v);
  }
  return ticks;
}

function fmtTick(v: number): string {
  if (v >= 1000) return `$${v / 1000}k`;
  return `$${v}`;
}

export function PricingBenchmarkLadderCard() {
  const { contentKey } = useCardIdentity();
  const strategy = useCapabilityStore((s) => s.strategyName('catalog', 'pricing_benchmarks'));
  const [sort, setSort] = useState<'median' | 'spread'>('median');
  const [view, setView] = useState<'ladder' | 'table'>('ladder');
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [thinExpanded, setThinExpanded] = useState<Set<string>>(new Set());
  const selection = usePricingBenchmarkSelectionStore((s) => s.selection);
  const setSelection = usePricingBenchmarkSelectionStore((s) => s.setSelection);
  const setBenchmarkContext = usePricingBenchmarkSelectionStore((s) => s.setBenchmarkContext);
  const inspectEntity = useGraphSelectionStore((s) => s.inspectEntity);
  const neighbourhoodStrategy = useCapabilityStore((s) => s.strategyName('catalog', 'pricing_benchmark_price_neighbourhood'));

  // Click-a-track: resolve the nearest REAL product for the clicked
  // price, then drive it through the app's real selection channel.
  // inspectEntity(), NOT selectNode() — we're inside a NAMED
  // explorerSelection ('benchmarks'), which wins over selectedNode.type
  // in useViewportSelection's entityType derivation (contentSel =
  // explorerSelection ?? selectedNode?.type ?? tabType). selectNode()
  // would resolve entityType to 'benchmarks', not 'product', and
  // VendorBriefCard would fetch the wrong strategy. inspectEntity()
  // short-circuits that (entityType = inspectedEntity?.type ?? contentSel)
  // — the exact same reason favourites-explorer-card.tsx and
  // product_search_results already use inspectEntity over selectNode.
  const handlePick = async (sel: PricePick) => {
    setSelection(sel);
    if (!neighbourhoodStrategy) return;
    const res = await api.getStrategyData<PriceNeighbourhoodResponse>(neighbourhoodStrategy, {
      category: sel.category, product_type: sel.productType, price: sel.price,
    });
    const nearest = res.data?.products?.[0];
    if (!res.success || !nearest) return;
    const { headline, subtitle } = describeBenchmarkPosition(sel, {
      percentile: res.data?.percentile ?? null, n: res.data?.n ?? 0, below: res.data?.below ?? 0,
    });
    const sentence = subtitle ? `${headline} ${subtitle}` : headline;
    inspectEntity({
      id: nearest.semantic_id, label: nearest.name ?? nearest.sku ?? nearest.semantic_id,
      type: 'product', properties: { list_price: nearest.list_price, image_url: nearest.image_url },
    });
    setBenchmarkContext({ productSid: nearest.semantic_id, sentence });
  };

  const { data, isLoading, error } = useStrategyData<CatalogPricingBenchmarksResponse>(
    strategy, { sort }, !!strategy,
  );
  const res = data?.data;
  const groups = res?.category_groups ?? [];
  const totalRows = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const toggle = (set: Set<string>, setSet: (s: Set<string>) => void, key: string) => {
    const next = new Set(set);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSet(next);
  };

  useCardExport(useMemo(() => ({
    content_key: contentKey ?? 'pricing_benchmark_ladder',
    status: (data ? 'loaded' : strategy ? 'loading' : 'idle') as 'loaded' | 'loading' | 'idle',
    query_key: null,
    text: `Pricing benchmark: ${groups.length} categories, ${totalRows} rows, sort=${sort}`,
    json: null,
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [contentKey, data, strategy, groups.length, totalRows, sort]));

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-destructive">
        <XCircle className="h-6 w-6 mb-2" />
        <p className={text.body}>Failed to load pricing benchmarks</p>
      </div>
    );
  }

  if (isLoading && groups.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ fontSize: surfaceBase.canvas }}>
      <div className="flex flex-col gap-1.5 px-4 py-3 border-b border-border shrink-0">
        <div className="flex items-center gap-3">
          <span className={text.heading}>Pricing Benchmarks</span>
          {res && (
            <span className={cn(text.muted, 'text-muted-foreground')}>
              {res.currency} · {res.price_basis} prices{res.as_of ? ` · ${res.as_of}` : ''}
            </span>
          )}
          <span className={cn(text.muted, 'text-muted-foreground')}>
            {groups.length} categories · {totalRows} rows
          </span>

          {view === 'ladder' && (
            <>
              <Legend />
              <div className="ml-auto flex items-center gap-1 rounded-md border border-border p-0.5">
                <button
                  type="button"
                  onClick={() => setSort('median')}
                  title="Market map — price shape, cheap to expensive"
                  className={cn('px-2 py-1 rounded text-xs',
                    sort === 'median' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
                >
                  Median
                </button>
                <button
                  type="button"
                  onClick={() => setSort('spread')}
                  title="VE targeting — highest ceiling/median spread first"
                  className={cn('px-2 py-1 rounded text-xs',
                    sort === 'spread' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
                >
                  Spread
                </button>
              </div>
            </>
          )}
          <div className={cn('flex items-center gap-1 rounded-md border border-border p-0.5', view === 'ladder' ? '' : 'ml-auto')}>
            <button
              type="button"
              onClick={() => setView('ladder')}
              className={cn('flex items-center gap-1 px-2 py-1 rounded text-xs',
                view === 'ladder' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
            >
              <LineChart className="h-3.5 w-3.5" /> Ladder
            </button>
            <button
              type="button"
              onClick={() => setView('table')}
              className={cn('flex items-center gap-1 px-2 py-1 rounded text-xs',
                view === 'table' ? 'bg-primary/10 text-primary' : 'text-muted-foreground hover:text-foreground')}
            >
              <Table2 className="h-3.5 w-3.5" /> Table
            </button>
          </div>
        </div>
        {res && res.total_flagged > 0 && (
          <div className={cn(text.muted, 'text-amber-600 dark:text-amber-400')}>
            {res.total_flagged} product types look like variants of others in the same category — review
            (marked "≈" below).
          </div>
        )}
      </div>

      <div className="flex-1 overflow-auto">
        {view === 'ladder' ? (
          <div className="p-2">
            {groups.map((g) => {
              const isOpen = expanded.has(g.category);
              const visible = g.rows.filter((r) => r.display_class !== 'insufficient' && !r.is_bare_category_fallback);
              const insufficient = g.rows.filter((r) => r.display_class === 'insufficient' && !r.is_bare_category_fallback);
              const fallback = g.rows.filter((r) => r.is_bare_category_fallback);
              const [scaleMin, scaleMax] = scaleFor(visible);
              const pct = (v: number) => {
                const lo = Math.log(scaleMin), hi = Math.log(scaleMax);
                return Math.min(100, Math.max(0, ((Math.log(Math.max(v, scaleMin)) - lo) / (hi - lo)) * 100));
              };
              const ticks = decadeTicks(scaleMin, scaleMax);
              const thinOpen = thinExpanded.has(g.category);
              return (
                <div key={g.category} className="border-b border-border/60">
                  <button
                    type="button"
                    onClick={() => toggle(expanded, setExpanded, g.category)}
                    className="w-full flex items-center gap-2 px-2 py-2 hover:bg-muted/40 rounded"
                  >
                    <ChevronRight className={cn('h-3.5 w-3.5 transition-transform text-muted-foreground', isOpen && 'rotate-90')} />
                    <span className={cn(text.heading, 'capitalize')}>{g.category}</span>
                    <span className={cn(text.muted, 'text-muted-foreground ml-2')}>
                      {g.rows.length} types · N={g.total_n}
                      {g.flagged_count > 0 && ` · ${g.flagged_count} flagged`}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="pl-6 pb-2 space-y-1.5">
                      <AxisRow ticks={ticks} pct={pct} />
                      {visible.map((r) => (
                        <LadderRow
                          key={`${g.category}-${r.product_type}`}
                          row={r} pct={pct} sort={sort} ticks={ticks}
                          scaleMin={scaleMin} scaleMax={scaleMax}
                          category={g.category}
                          onPick={handlePick}
                          selectedPrice={
                            selection && selection.category === g.category && selection.productType === r.product_type
                              ? selection.price : null
                          }
                        />
                      ))}
                      {insufficient.length > 0 && (
                        <div>
                          <button
                            type="button"
                            onClick={() => toggle(thinExpanded, setThinExpanded, g.category)}
                            className={cn(text.muted, 'text-muted-foreground hover:text-foreground flex items-center gap-1')}
                          >
                            <ChevronRight className={cn('h-3 w-3 transition-transform', thinOpen && 'rotate-90')} />
                            {insufficient.length} types below threshold —{' '}
                            {insufficient.slice(0, 3).map((r) => r.product_type).join(', ')}
                            {insufficient.length > 3 ? '…' : ''} {thinOpen ? '(collapse)' : '(expand)'}
                          </button>
                          {thinOpen && (
                            <div className="mt-1 pl-4 flex flex-wrap gap-x-3 gap-y-1">
                              {insufficient.map((r) => (
                                <span key={r.product_type} className={cn(text.muted, 'text-muted-foreground')}>
                                  {r.product_type} (N={r.n})
                                  {r.flagged_variant_of && <span title={`Looks like a variant of ${r.flagged_variant_of}`}> ≈{r.flagged_variant_of}</span>}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      {fallback.map((r) => (
                        <LadderRow
                          key={`${g.category}-fallback`}
                          row={r} pct={pct} sort={sort} ticks={ticks}
                          scaleMin={scaleMin} scaleMax={scaleMax}
                          category={g.category}
                          onPick={handlePick}
                          selectedPrice={
                            selection && selection.category === g.category && selection.productType === null
                              ? selection.price : null
                          }
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
            {groups.length === 0 && (
              <div className="text-center text-muted-foreground py-12">No benchmarks minted yet</div>
            )}
          </div>
        ) : (
          <table className="w-full text-xs border-collapse">
            <thead className="sticky top-0 bg-card z-10">
              <tr className="border-b border-border">
                <th className="text-left px-3 py-2">Category</th>
                <th className="text-left px-3 py-2">Product type</th>
                <th className="text-right px-3 py-2">N</th>
                <th className="text-right px-3 py-2">Budget</th>
                <th className="text-right px-3 py-2">Mid</th>
                <th className="text-right px-3 py-2">Premium</th>
                <th className="text-right px-3 py-2">Ceiling</th>
              </tr>
            </thead>
            <tbody>
              {groups.flatMap((g) => g.rows.map((r) => (
                <tr key={`${g.category}-${r.product_type ?? 'fallback'}`} className="border-b border-border/60">
                  <td className="px-3 py-1.5 capitalize">{g.category}</td>
                  <td className={cn('px-3 py-1.5 text-muted-foreground', r.is_bare_category_fallback && 'italic')}>
                    {r.product_type ?? '(all)'}
                    {r.flagged_variant_of && <span className="text-amber-600 dark:text-amber-400"> ≈{r.flagged_variant_of}</span>}
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{r.n}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtPrice(r.budget_median)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtPrice(r.mid_median)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtPrice(r.premium_median)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtPrice(r.ceiling)}</td>
                </tr>
              )))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// Ordered ramp, one hue, light -> dark — Budget -> Mid -> Premium is a
// scale, not three unrelated categories, so it gets ordered color
// (Economist/FT/Bloomberg convention), not a categorical red/green/amber
// that reads as a traffic light. Flagged three times before this landed.
const TIER_COLOR = { budget: 'bg-sky-300', mid: 'bg-sky-500', premium: 'bg-sky-800' } as const;

function Legend() {
  return (
    <div className={cn(text.muted, 'flex items-center gap-3 text-muted-foreground')}>
      <span className="flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', TIER_COLOR.budget)} /> Budget</span>
      <span className="flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', TIER_COLOR.mid)} /> Mid</span>
      <span className="flex items-center gap-1"><span className={cn('h-2 w-2 rounded-full', TIER_COLOR.premium)} /> Premium</span>
      <span className="flex items-center gap-1"><span className="h-3.5 w-px bg-muted-foreground/60" /> Ceiling</span>
    </div>
  );
}

// The axis this chart was missing: real, labelled gridlines. Mirrors a
// row's own layout (label spacer / track / value spacer) so its tick
// labels land exactly under each row's gridlines below it.
function AxisRow({ ticks, pct }: { ticks: number[]; pct: (v: number) => number }) {
  return (
    <div className="flex items-center gap-3 pb-1 mb-1 border-b border-border/40">
      <div className="w-40 shrink-0" />
      <div className="relative flex-1 h-4">
        {ticks.map((t) => (
          <span
            key={t}
            className={cn(text.muted, 'absolute -translate-x-1/2 text-muted-foreground tabular-nums')}
            style={{ left: `${pct(t)}%` }}
          >
            {fmtTick(t)}
          </span>
        ))}
      </div>
      <div className="w-32 shrink-0" />
    </div>
  );
}

function LadderRow({ row: r, pct, sort, scaleMin, scaleMax, category, ticks, onPick, selectedPrice }: {
  row: BenchmarkRow; pct: (v: number) => number; sort: 'median' | 'spread';
  scaleMin: number; scaleMax: number; category: string; ticks: number[];
  onPick: (sel: PricePick) => void;
  selectedPrice: number | null;
}) {
  const label = r.product_type ?? '(all — bare category)';
  const spread = r.ceiling != null && r.median ? r.ceiling / r.median : null;
  // Whole track is clickable, not just the dots — a click anywhere is a
  // "what's around this price" probe (the gap between Mid and Premium,
  // say, is exactly where substitution room lives and has no dot of its
  // own to click). Inverse of the log-scale `pct` placement function.
  const invert = (clickPct: number): number => {
    const lo = Math.log(scaleMin), hi = Math.log(scaleMax);
    return Math.exp(lo + (clickPct / 100) * (hi - lo));
  };
  const handleTrackClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (r.display_class === 'insufficient') return;
    const bounds = e.currentTarget.getBoundingClientRect();
    const clickPct = ((e.clientX - bounds.left) / bounds.width) * 100;
    onPick({
      category, productType: r.product_type, price: invert(clickPct),
      n: r.n, displayClass: r.display_class,
      budgetMedian: r.budget_median, midMedian: r.mid_median, premiumMedian: r.premium_median,
    });
  };
  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          text.body, 'w-40 shrink-0 truncate',
          r.display_class !== 'full' && 'text-muted-foreground',
          r.is_bare_category_fallback && 'italic',
        )}
      >
        {label}
        {r.flagged_variant_of && (
          <span
            title={`Looks like a variant of "${r.flagged_variant_of}" — review`}
            className="text-amber-600 dark:text-amber-400"
          > ≈{r.flagged_variant_of}</span>
        )}
      </div>
      <div
        onClick={handleTrackClick}
        className={cn(
          'relative flex-1 h-6',
          r.is_bare_category_fallback && 'opacity-60',
          r.display_class !== 'insufficient' && 'cursor-crosshair',
        )}
        title={r.display_class !== 'insufficient' ? 'Click to see products near this price' : undefined}
      >
        {ticks.map((t) => (
          <div
            key={t}
            className="absolute top-0 bottom-0 w-px bg-border/50"
            style={{ left: `${pct(t)}%` }}
          />
        ))}
        <div
          className="absolute inset-y-1/2 left-0 right-0 h-px"
          style={r.is_bare_category_fallback
            ? { backgroundImage: 'repeating-linear-gradient(90deg, hsl(var(--border)) 0 4px, transparent 4px 8px)' }
            : undefined}
        >
          {!r.is_bare_category_fallback && <div className="h-px bg-border" />}
        </div>
        {selectedPrice != null && (
          <div
            className="absolute top-1/2 -translate-y-1/2 h-4 w-0.5 bg-foreground rounded-full pointer-events-none"
            style={{ left: `${pct(selectedPrice)}%` }}
          />
        )}
        {r.display_class === 'full' ? (
          <>
            <div
              className="absolute top-1/2 h-px bg-sky-500/30"
              style={{
                left: `${pct(r.budget_median!)}%`,
                width: `${Math.max(0, pct(r.premium_median!) - pct(r.budget_median!))}%`,
              }}
            />
            <Dot pctLeft={pct(r.budget_median!)} tone="budget" title={`Budget · ${fmtPrice(r.budget_median)}`} />
            <Dot pctLeft={pct(r.mid_median!)} tone="mid" title={`Mid · ${fmtPrice(r.mid_median)}`} />
            <Dot pctLeft={pct(r.premium_median!)} tone="premium" title={`Premium · ${fmtPrice(r.premium_median)}`} />
            {r.ceiling != null && (
              <div
                title={`Ceiling (gold-plating above) · ${fmtPrice(r.ceiling)}`}
                className="absolute top-1/2 -translate-y-1/2 h-3.5 w-px bg-muted-foreground/60"
                style={{ left: `${pct(r.ceiling)}%` }}
              />
            )}
          </>
        ) : r.display_class === 'thin' && r.median != null ? (
          <Dot pctLeft={pct(r.median)} tone="mid" muted title={`Median · ${fmtPrice(r.median)} (N=${r.n}, below tier gate)`} />
        ) : null}
      </div>
      <div className={cn(text.muted, 'w-32 shrink-0 text-right text-muted-foreground tabular-nums')}>
        {sort === 'spread' && r.display_class === 'full' && spread != null
          ? `${spread.toFixed(1)}× spread`
          : r.display_class === 'full'
          ? `${fmtPrice(r.budget_median)} · ${fmtPrice(r.mid_median)} · ${fmtPrice(r.premium_median)}`
          : r.display_class === 'thin'
          ? `${fmtPrice(r.median)} (N=${r.n})`
          : `N=${r.n}`}
      </div>
    </div>
  );
}

function Dot({ pctLeft, tone, title, muted }: {
  pctLeft: number; tone: 'budget' | 'mid' | 'premium'; title: string; muted?: boolean;
}) {
  const color = muted ? 'bg-muted-foreground/50' : TIER_COLOR[tone];
  return (
    <div
      title={title}
      className={cn('absolute top-1/2 -translate-y-1/2 -translate-x-1/2 h-2.5 w-2.5 rounded-full ring-2 ring-background', color)}
      style={{ left: `${pctLeft}%` }}
    />
  );
}
